import { NavLink, SearchBox, SortField } from '@/components/controls';
import {
  useNoMemo,
  useOptionLookup,
  useOptions,
  useRfidCards,
  useUserInfo,
} from '@/hooks';
import { downloadCSV, downloadJSON, get, isAuthorised, sortFn } from '@/utils';
import { useDallasKeys, useRfidCardCategory } from '@/utils/config';
import {
  Add as AddIcon,
  Nfc as NfcIcon,
  Settings as SettingsIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { format } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { Link, Outlet, useParams } from 'react-router';

export function RfidCards() {
  const { id } = useParams();
  const {
    query: { data: rfidCards },
  } = useRfidCards();
  const rfidCardTypes = useOptionLookup('rfidCardType');
  const {
    query: { data: tripClassifications },
  } = useOptions('tripClassification');
  const [searchText, setSearchText] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [sortBy, setSortBy] = useState('reference');
  const [sortDesc, setSortDesc] = useState(false);
  const [filter, setFilter] = useState({
    type: '',
    category: '',
    assignments: '',
  });
  const parentRef = useRef();
  const {
    query: { data: userInfo },
  } = useUserInfo();
  const canCreate = isAuthorised(userInfo?.authorisation, 'people', true);

  const sortOptions = [
    { label: 'Serial Number', value: 'reference' },
    ...(useRfidCardCategory ? [{ label: 'Category', value: 'category' }] : []),
    { label: 'Type', value: 'type' },
    { label: 'Label', value: 'label' },
    { label: 'Last Used Time', value: 'time' },
    { label: 'Assignments', value: 'assignmentCount' },
  ];

  const filteredList = useMemo(() => {
    return rfidCards
      .filter((item) => {
        const searchString = `${item.reference}\n${item.category}\n${item.type}\n${item.label}`;

        return (
          searchString.indexOf(searchText.toLowerCase()) > -1 ||
          searchText === ''
        );
      })
      .filter((item) => !filter.type || filter.type === item.type)
      .filter((item) => !filter.category || filter.category === item.category)
      .filter(
        (item) =>
          !filter.assignments ||
          (filter.assignments === 'none' && item.assignmentCount === 0) ||
          (filter.assignments === 'multiple' && item.assignmentCount > 1),
      )
      .sort(sortFn(sortBy, sortDesc));
  }, [rfidCards, sortBy, sortDesc, searchText, filter]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: filteredList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 10,
  });
  const virtualItems = useNoMemo(() => rowVirtualizer.getVirtualItems());

  function handleSettingsToggle() {
    setShowSettings(!showSettings);
  }

  function handleSearchChange(event) {
    setSearchText(event.target.value);
  }

  function handleSortByChange(event) {
    setSortBy(event.target.value);
  }

  function handleSortToggle() {
    setSortDesc(!sortDesc);
  }

  const handleFilterChange = (field) => (event) => {
    setFilter({ ...filter, [field]: event.target.value });
  };

  function handleCsvClick() {
    downloadCSV(
      filteredList,
      useDallasKeys ? 'dallas-keys.csv' : 'rfid-cards.csv',
      [
        { label: 'Serial Number', key: 'reference' },
        ...(useRfidCardCategory
          ? [{ label: 'Category', key: 'category' }]
          : []),
        { label: 'Type', key: 'type' },
        { label: 'Label', key: 'label' },
        { label: 'Last Used Time', key: 'time' },
        { label: 'Assignments', key: 'assignmentCount' },
      ],
    );
  }

  function handleJsonClick() {
    downloadJSON(
      filteredList,
      useDallasKeys ? 'dallas-keys.json' : 'rfid-cards.json',
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 48px)' }}>
      <title>IR3 | {useDallasKeys ? 'Dallas Keys' : 'RFID Cards'}</title>
      <Stack
        sx={{
          width: 280,
          borderRight: 1,
          borderColor: 'divider',
          flexShrink: 0,
          height: 1,
        }}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{ p: 1, alignItems: 'center' }}
          >
            <SearchBox
              value={searchText}
              onChange={handleSearchChange}
              count={`${filteredList.length}/${rfidCards.length}`}
              sx={{ flexGrow: 1 }}
            />
            <Tooltip title={showSettings ? 'Hide settings' : 'Show settings'}>
              <IconButton size="small" onClick={handleSettingsToggle}>
                <SettingsIcon
                  fontSize="inherit"
                  color={showSettings ? 'primary' : 'inherit'}
                />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Tooltip title="Add new">
                <IconButton component={Link} to="new" size="small">
                  <Avatar
                    sx={{
                      color: 'secondary.contrastText',
                      bgcolor: 'secondary.main',
                      width: 24,
                      height: 24,
                      fontSize: 16,
                    }}
                  >
                    <AddIcon fontSize="inherit" />
                  </Avatar>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Collapse in={showSettings} unmountOnExit>
            <Stack sx={{ p: 1 }} spacing={1}>
              <SortField
                sortBy={sortBy}
                onSortByChange={handleSortByChange}
                sortDesc={sortDesc}
                onSortDescToggle={handleSortToggle}
                options={sortOptions}
              />
            </Stack>
            <Stack sx={{ px: 1, pb: 1 }} spacing={1}>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Filters
                </Typography>
              </Divider>
              {useRfidCardCategory && (
                <TextField
                  size="small"
                  fullWidth
                  select
                  label="Classification"
                  value={filter.category}
                  onChange={handleFilterChange('category')}
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>
                  {(tripClassifications ?? []).map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                size="small"
                fullWidth
                select
                label="Type"
                value={filter.type}
                onChange={handleFilterChange('type')}
              >
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                {Object.entries(rfidCardTypes).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                fullWidth
                select
                label="Assignments"
                value={filter.assignments}
                onChange={handleFilterChange('assignments')}
              >
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="multiple">Multiple</MenuItem>
              </TextField>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Export
                </Typography>
              </Divider>
              <Stack
                direction="row"
                // sx={{ p: 1 }}
                spacing={1}
                sx={{ justifyContent: 'center' }}
              >
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  disableElevation
                  onClick={handleCsvClick}
                >
                  CSV
                </Button>
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  disableElevation
                  onClick={handleJsonClick}
                >
                  JSON
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </Box>
        <Divider />
        <Box ref={parentRef} sx={{ overflow: 'auto', height: 1 }}>
          <Box
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map(({ index, size, start }) => {
              const item = filteredList[index];

              function getBadgeColor(count) {
                return count === 0
                  ? 'warning'
                  : count === 1
                    ? 'success'
                    : 'error';
              }

              function getSecondaryText() {
                switch (sortBy) {
                  case 'reference':
                  case 'assignmentCount':
                  case 'type':
                    return rfidCardTypes[item.type] ?? item.type;
                  case 'time':
                    return format(
                      new Date(get(item, sortBy)),
                      'dd/MM/yyyy HH:mm:ss',
                    );
                  default:
                    return get(item, sortBy);
                }
              }

              return (
                <ListItemButton
                  dense
                  key={index}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${size}px`,
                    transform: `translateY(${start}px)`,
                  }}
                  component={NavLink}
                  to={`/resources/${
                    useDallasKeys ? 'dallaskeys' : 'rfidcards'
                  }/${item.reference}`}
                  selected={id === item.reference}
                >
                  <ListItemAvatar>
                    <Badge
                      showZero
                      badgeContent={item.assignmentCount}
                      color={getBadgeColor(item.assignmentCount)}
                    >
                      <Avatar>
                        {useDallasKeys ? <VpnKeyIcon /> : <NfcIcon />}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.reference}
                    secondary={getSecondaryText()}
                  />
                </ListItemButton>
              );
            })}
          </Box>
        </Box>
      </Stack>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
